import api from "@/services/api";
import { getEndpoint } from "./schemaProvider";
import { toIdLabels } from "./labeller";
import { hasItemPage } from "@/routes/itemRoutes";
import store from "@/store/store";

const pathVariablesRegex = /[^{}]?{([a-zA-Z0-9]+)}/g;

const actions = {
    async get(path, model) {
        const response = await api.get(path, { params: model });
        return response.data;
    },

    async post(path, model, followLocation = false) {
        const response = await api.post(path, model ?? null);
        return await commandResponse(response, followLocation);
    },

    async put(path, model, followLocation = false) {
        const response = await api.put(path, model ?? null);
        return await commandResponse(response, followLocation);
    },

    async patch(path, model, followLocation = false) {
        const response = await api.patch(path, model ?? null);
        return await commandResponse(response, followLocation);
    },

    async delete(path) {
        await api.delete(path);
    }
};

async function commandResponse(response, followLocation) {
    if (followLocation) {
        return await getViaLocationHeader(response);
    }
    return response.data;
}

async function getViaLocationHeader(response) {
    const location = response.headers.get("location");
    if (!location) {
        throw new Error("No location header found in response");
    }
    const getResponse = await api.get(location);
    return getResponse.data;
}

function getPathVariables(path) {
    const matches = [...path.matchAll(pathVariablesRegex)];
    return matches.map(match => match[1]);
}

function applyPathVariables(path, pathVariables, model) {
    let result = path;
    pathVariables.forEach(v => {
        result = result.replace("{" + v + "}", model[v]);
    });
    return result;
}

function removePathVariables(model, pathVariables) {
    const result = {};

    Object
        .keys(model)
        .filter(key => !pathVariables.includes(key))
        .forEach(key => {
            result[key] = model[key];
        });

    return result;
}

function hasPolicy(policy) {
    return store.getters["authentication/hasPolicy"](policy);
}

function canDo(entityKey, endpointKey, scope) {
    const endpoint = getEndpoint(entityKey, endpointKey, scope);
    if (!endpoint) {
        return false;
    }

    return endpoint.policies == null || endpoint.policies.every(policy => hasPolicy(policy));
}

export async function call(entityKey, endpoint, model, scope, followLocation = false) {
    const endpointInfo = getEndpoint(entityKey, endpoint, scope);
    if (endpointInfo == null) {
        throw new Error(`Unable to find endpoint: ${endpoint} for entity: ${entityKey}`);
    }

    let path = endpointInfo.path;

    if (model && !Array.isArray(model)) {
        // Add the variables which go into the path. e.g. /api/buckets/{id} -> /api/buckets/1
        const pathVariables = getPathVariables(path);
        path = applyPathVariables(path, pathVariables, model);

        // Remove those path variables from the model. e.g. to avoid /api/buckets/1?id=1
        model = removePathVariables(model, pathVariables);
    }

    const action = actions[endpointInfo.method];
    if (followLocation && !canDo(entityKey, "get", scope)) {
        followLocation = false;
    }
    return await action(path, model, followLocation);
}

export async function list(entityKey, scope, model) {
    if (model == null) model = {};
    return await call(entityKey, "list", model, scope);
}

export async function idLabels(entityKey, model) {
    const result = await list(entityKey, model);
    return toIdLabels(entityKey, result.items);
}

export async function get(entityKey, scope, id) {
    if (id == null) {
        throw new Error(`Unable to retrieve ${entityKey} with empty id`);
    }

    return await call(entityKey, "get", { id }, scope);
}

export async function getMany(entityKey, scope, ids) {
    return await call(entityKey, "getMany", ids, scope);
}

export async function add(entityKey, scope, model, followLocation = true) {
    const result = await call(entityKey, "add", model, scope, followLocation);
    if (followLocation && !result) {
        throw new Error(`Unable to get result after adding ${entityKey}`);
    }
    return result;
}

export async function updateNoFollow(entityKey, scope, model) {
    await call(entityKey, "update", model, scope, false);
}

export async function update(entityKey, scope, model, followLocation = true) {
    const result = await call(entityKey, "update", model, scope, followLocation);
    if (followLocation && !result) {
        throw new Error(`Unable to get result after updating ${entityKey}`);
    }
    return result;
}

export async function save(entityKey, scope, model, followLocation = true) {
    if (model.id == null) {
        return add(entityKey, scope, model, followLocation);
    }
    return update(entityKey, scope, model, followLocation);
}

export async function delete_(entityKey, scope, id) {
    return await call(entityKey, "delete", { id }, scope);
}

export async function command(entityKey, scope, model) {
    return await call(entityKey, "command", model, scope);
}

export async function query(entityKey, scope, model) {
    return await call(entityKey, "query", model, scope);
}

export function canAdd(entityKey, scope) {
    return canDo(entityKey, "add", scope);
}

export function canEdit(entityKey, scope) {
    return canDo(entityKey, "update", scope);
}

export function canDelete(entityKey, scope) {
    return canDo(entityKey, "delete", scope);
}

export function canGet(entityKey, scope) {
    return canDo(entityKey, "get", scope);
}

export function canGetMany(entityKey, scope) {
    return canDo(entityKey, "getMany", scope);
}

export function canList(entityKey, scope) {
    return canDo(entityKey, "list", scope);
}

export function canCommand(entityKey, scope) {
    return canDo(entityKey, "command", scope);
}

export function canQuery(entityKey, scope) {
    return canDo(entityKey, "query", scope);
}

export function canViewItem(entityKey) {
    return hasItemPage(entityKey);
}

export default {
    call,
    list,
    idLabels,
    get,
    getMany,
    add,
    update,
    updateNoFollow,
    save,
    delete: delete_,
    command,
    query,
    canAdd,
    canEdit,
    canDelete,
    canGet,
    canGetMany,
    canList,
    canCommand,
    canQuery,
    canViewItem
}

